/**
 * Bomba submersa no SCADA 3D: corte esquemático do poço abaixo do cavalete — Nexora Grid (retorno de 18/10/2026).
 *
 * Criado em: 18/10/2026 · Última modificação: 25/10/2026 · Versão: 1.0.1
 *
 * Bomba submersa: a bomba fica dentro do poço, abaixo do círculo bege (laje/boca do poço) do
 * cavalete. Este módulo monta, abaixo da boca do poço do modelo da estação, o revestimento, a água a partir do nível
 * estático, o nível dinâmico, a seção filtrante, a edutora e o conjunto bomba + motor no crivo — com as profundidades
 * reais da ficha (NBR 12212) nos rótulos. O desenho é fora de escala na vertical (o poço de 100 m vira alguns metros
 * de desenho) e aparece através do terreno ("raio X") só quando a câmera está perto.
 */
import * as THREE from "three";
import type { DadosPoco } from "./rede";

/** Altura do desenho (unidades do modelo, antes da escala de distância). */
export const PROFUNDIDADE_DESENHO = 8;
const PADRAO: Required<DadosPoco> = { profundidade_poco_m: 100, nivel_estatico_m: 20, nivel_dinamico_m: 40, profundidade_instalacao_m: 55, topo_filtros_m: 70 };

function material(cor: string, opacidade = 0.9): THREE.MeshBasicMaterial {
  // visível através do terreno, sem escrever profundidade (não esconde a estação acima)
  return new THREE.MeshBasicMaterial({ color: cor, transparent: true, opacity: opacidade, depthTest: false, depthWrite: false });
}

function rotulo(texto: string, cor: string, ancoraX = 0): THREE.Sprite {
  const fonte = "600 34px 'IBM Plex Mono', monospace";
  const medida = document.createElement("canvas").getContext("2d")!;
  medida.font = fonte;
  const tela = document.createElement("canvas");
  tela.width = Math.ceil(medida.measureText(texto).width) + 24;   // caixa do tamanho do texto
  tela.height = 64;
  const ctx = tela.getContext("2d")!;
  ctx.fillStyle = "rgba(5, 11, 18, 0.78)";
  ctx.fillRect(0, 0, tela.width, tela.height);
  ctx.font = fonte;
  ctx.fillStyle = cor;
  ctx.textBaseline = "middle";
  ctx.fillText(texto, 12, 33);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(tela), depthTest: false, depthWrite: false, transparent: true }));
  sprite.scale.set(0.3 * tela.width / tela.height, 0.3, 1);
  sprite.renderOrder = 31;
  sprite.center.set(ancoraX, 0.5);   // 0 = texto à direita do ponto; 1 = à esquerda
  return sprite;
}

const cilindro = (raio: number, altura: number, cor: string, opacidade?: number, aberto = false) => {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(raio, raio, altura, 20, 1, aberto), material(cor, opacidade));
  m.renderOrder = 30;
  return m;
};

/**
 * Corte do poço com a origem na boca (y = 0, descendo em -y).
 *
 * Args:
 *   poco: dados da ficha (ausentes = valores típicos, e o título avisa que a ficha está sem os dados do poço).
 */
export function montarCorte(poco: DadosPoco | null | undefined): THREE.Group {
  const temDados = Boolean(poco?.profundidade_poco_m);
  const d: Required<DadosPoco> = { ...PADRAO, ...Object.fromEntries(Object.entries(poco ?? {}).filter(([, v]) => v !== null && v !== undefined)) };
  const fundo = d.profundidade_poco_m;
  const y = (m: number) => -(Math.min(m, fundo) / fundo) * PROFUNDIDADE_DESENHO;
  const g = new THREE.Group();
  g.name = "corte_poco";
  g.userData.semHolograma = true;
  const revestimento = cilindro(0.2, PROFUNDIDADE_DESENHO, "#9ca3af", 0.35, true);
  revestimento.position.y = -PROFUNDIDADE_DESENHO / 2;
  g.add(revestimento);
  const alturaAgua = PROFUNDIDADE_DESENHO + y(d.nivel_estatico_m);
  const agua = cilindro(0.17, alturaAgua, "#1e88e5", 0.45);
  agua.position.y = y(d.nivel_estatico_m) - alturaAgua / 2;
  g.add(agua);
  const alturaFiltros = PROFUNDIDADE_DESENHO + y(d.topo_filtros_m);
  if (alturaFiltros > 0.05) {
    const filtros = cilindro(0.23, alturaFiltros, "#94a3b8", 0.25, true);
    filtros.position.y = y(d.topo_filtros_m) - alturaFiltros / 2;
    g.add(filtros);
  }
  const anel = (m: number, cor: string) => {
    const a = new THREE.Mesh(new THREE.TorusGeometry(0.26, 0.025, 8, 32), material(cor, 1));
    a.rotation.x = Math.PI / 2;
    a.position.y = y(m);
    a.renderOrder = 31;
    g.add(a);
  };
  anel(d.nivel_estatico_m, "#4f8fe6");
  anel(d.nivel_dinamico_m, "#22d3ee");
  // bomba (corpo com os estágios) e motor abaixo; a edutora sobe até a boca
  const yBomba = y(d.profundidade_instalacao_m);
  const edutor = cilindro(0.045, -yBomba, "#2e8b57", 1);
  edutor.position.y = yBomba / 2;
  g.add(edutor);
  const corpo = cilindro(0.11, 0.55, "#3a7bd5", 1);
  corpo.position.y = yBomba - 0.275 + 0.55;
  const motor = cilindro(0.11, 0.5, "#1f3b63", 1);
  motor.position.y = yBomba - 0.25;
  g.add(corpo, motor);
  const fmt = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} m`;
  const marcas: [number, string, string][] = [
    [d.nivel_estatico_m, `NE ${fmt(d.nivel_estatico_m)}`, "#7cb3ff"], [d.nivel_dinamico_m, `ND ${fmt(d.nivel_dinamico_m)}`, "#22d3ee"],
    [d.profundidade_instalacao_m, `bomba ${fmt(d.profundidade_instalacao_m)}`, "#c4a7ff"], [fundo, `fundo ${fmt(fundo)}`, "#cbd5e1"]];
  // rótulos à esquerda do poço e título abaixo do fundo: o lado direito fica livre para o quadro de dados
  marcas.forEach(([m, texto, cor]) => { const s = rotulo(texto, cor, 1); s.position.set(-0.4, y(m), 0); g.add(s); });
  const titulo = rotulo(temDados ? "bomba submersa · corte fora de escala" : "bomba submersa · ficha sem dados do poço", temDados ? "#c8f3ff" : "#f4c430");
  titulo.center.set(0.5, 0.5);
  titulo.position.set(0, -PROFUNDIDADE_DESENHO - 0.45, 0);
  g.add(titulo);
  g.traverse((o) => { o.userData.semHolograma = true; }); // materiais próprios: o efeito holograma não mexe
  return g;
}
